import { useLazyQuery } from "@apollo/client/react"; import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, InputAdornment } from '@mui/material';
import toast, { Toaster } from 'react-hot-toast';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GET_ALL_JUSTIFICACION_CONDICIONAL } from '../../../data/graphQL/queries/AdminBloqueo/getAllJustificacionCondicional';
import { GET_ALL_ESTADO_BUM } from '../../../data/graphQL/queries/AdminBloqueo/getAllEstadoCondicionalBUM';
import { GET_ALL_ESTADO_COMERCIAL } from '../../../data/graphQL/queries/AdminBloqueo/getAllEstadoCondicionalComercial';
import { GET_ALL_ESTADO_MEDICO } from '../../../data/graphQL/queries/AdminBloqueo/getAllEstadoCondicionalMedico';
import { GET_JUSTIFICACIONES_EXPORT, adaptJustificacionesToExportArray } from '../../../data/graphQL/queries/AdminBloqueo/getJustificacionesToExport';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { usePersistFilters } from '../../../hooks/usePersistFilters';

import { CustomButton } from '../../../components/customButton/CustomButton';
import { CustomChip } from '../../../components/chip/Chip';
import { downloadExcel } from '../../../utils/downloadExcel';


export const AdminBloqueoAll = ({ setChildrenAction }) => {
  const navigate = useNavigate();
  const [justificaciones, setJustificaciones] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [estadosBUM, setEstadosBUM] = useState([]);
  const [estadosMedico, setEstadosMedico] = useState([]);
  const [estadosComercial, setEstadosComercial] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);
  const [textFilter, setTextFilter] = useState(null);
  
  const location = useLocation();

  const ESTADO_AP = "AP";
  const ESTADO_PE = "PE";
  const ESTADO_RE = "RE";

  const getChipEstado = (estado) => {
    switch (estado) {
      case ESTADO_PE:
        return (
          <CustomChip label={estado} isYellow title='Pendiente' />);

      case ESTADO_AP:
        return (
          <CustomChip label={estado} isGreen title='Aprobado' />);

      case ESTADO_RE:
        return (
          <CustomChip label={estado} isRed title='Rechazado' />);

      default: return;
    }
  };

  const { filters, handleChangeFilters } = usePersistFilters({
    tipoJustificacion: "TODOS",
    idEvaluacion: "",
    idOrdenPedido: "",
    idEstadoComercial: 1, // pendiente
    idEstadoMedico: 1, // pendiente
    idEstadoBUM: 1 // pendiente
  }, "justificacionGrillaFilters"); 

  useEffect(() => {
    setChildrenAction(() =>
      <CustomButton
        label='Exportar'
        loading={exportLoading}
        onClick={exportExcel}
        variant='contained'>

        Exportar
      </CustomButton>
    );
  }, [setChildrenAction, justificaciones, exportLoading]);

  const [getAllJustificacion, { data: dataJustif, loading: bloqueosLoading }] = useLazyQuery(GET_ALL_JUSTIFICACION_CONDICIONAL, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataJustif) return;
    setDatosOriginal(dataJustif?.allJustificacionCondicional?.valorEsperado)
    setJustificaciones(dataJustif?.allJustificacionCondicional?.valorEsperado);
  }, [dataJustif]);

  const [getAllEstadoBUM, { data: dataEstadoBum, loading: estadoBUMLoading }] = useLazyQuery(GET_ALL_ESTADO_BUM, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEstadoBum) return;
    setEstadosBUM(dataEstadoBum?.allEstadoCondicionalBUM);
  }, [dataEstadoBum]);

  const [getAllEstadoMedico, { data: dataEstadoMedico, loading: estadoMedicoLoading }] = useLazyQuery(GET_ALL_ESTADO_MEDICO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEstadoMedico) return;
    setEstadosMedico(dataEstadoMedico?.allEstadoCondicionalMedico);
  }, [dataEstadoMedico]);

  const [getAllEstadoComercial, { data: dataEstadoComercial, loading: estadoComercialLoading }] = useLazyQuery(GET_ALL_ESTADO_COMERCIAL, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEstadoComercial) return;
    setEstadosComercial(dataEstadoComercial?.allEstadoCondicionalComercial);
  }, [dataEstadoComercial]);

  const [getJustificacionesToExport] = useLazyQuery(GET_JUSTIFICACIONES_EXPORT, {
    fetchPolicy: 'no-cache'
  });

  const exportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await getJustificacionesToExport({
        variables: {
          tipoJustificacion: filters.tipoJustificacion,
          idEvaluacion: filters.idEvaluacion !== "" ? parseInt(filters.idEvaluacion) : 0,
          idOrdenPedido: filters.idOrdenPedido !== "" ? parseInt(filters.idOrdenPedido) : 0,
          idEstadoComercial: filters.idEstadoComercial,
          idEstadoMedico: filters.idEstadoMedico,
          idEstadoBUM: filters.idEstadoBUM
        }
      });

      const data = adaptJustificacionesToExportArray(response.data.exportarJustificaciones.valorEsperado);

      downloadExcel({
        fileName: 'Bloqueos',
        sheet: "Bloqueos",
        tablePayload: {
          header: excelColumns,
          body: data
        }
      });

      toast.success('Exportación realizada con éxito!');
    } catch (error) {
      toast.error(error?.message || 'Ocurrió un error al intentar exportar.');

    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (!estadoBUMLoading) {
      getAllEstadoBUM();
    }
    if (!estadoComercialLoading) {
      getAllEstadoComercial();
    }
    if (!estadoMedicoLoading) {
      getAllEstadoMedico();
    }
    if (!bloqueosLoading) {
      getAllJustificacion({
        variables: {
          tipoJustificacion: filters.tipoJustificacion,
          idEvaluacion: filters.idEvaluacion !== "" ? parseInt(filters.idEvaluacion) : 0,
          idOrdenPedido: filters.idOrdenPedido !== "" ? parseInt(filters.idOrdenPedido) : 0,
          idEstadoComercial: filters.idEstadoComercial,
          idEstadoMedico: filters.idEstadoMedico,
          idEstadoBUM: filters.idEstadoBUM
        }
      });
    }
  }, []);

  const handleView = (row) => {
    const ruta = location.pathname;

    navigate(ruta + '/justificacionBloqueo', {
      state: {
        justificacion: row
      }
    });
  };

  const handleSearch = () => {
    setTextFilter('');
    getAllJustificacion({
      variables: {
        tipoJustificacion: filters.tipoJustificacion,
        idEvaluacion: filters.idEvaluacion !== "" ? parseInt(filters.idEvaluacion) : 0,
        idOrdenPedido: filters.idOrdenPedido !== "" ? parseInt(filters.idOrdenPedido) : 0,
        idEstadoComercial: filters.idEstadoComercial,
        idEstadoMedico: filters.idEstadoMedico,
        idEstadoBUM: filters.idEstadoBUM
      }
    });
  };

  const handleTextFilter = () => {
    const inputBuscar = textFilter?.toString().toUpperCase();
    if (inputBuscar?.length >= 2) {
      setJustificaciones(datosOriginal?.filter((datoOriginal) => {
        const idEvaluacionBuscar = datoOriginal.idEvaluacion.toString();
        const idOrdenPedidoBuscar = datoOriginal.idOrdenPedido.toString();
        const delegacionBuscar = datoOriginal.descDelegacion.toString();
        const idPacienteBuscar = datoOriginal.idPaciente.toString();
        const pacienteBuscar = datoOriginal.paciente.toString();
        const coberturaBuscar = datoOriginal.cobertura.toString();

        return (
          idEvaluacionBuscar.includes(inputBuscar) ||
          delegacionBuscar.includes(inputBuscar) ||
          idPacienteBuscar.includes(inputBuscar) ||
          pacienteBuscar.includes(inputBuscar) ||
          coberturaBuscar.includes(inputBuscar) ||
          idOrdenPedidoBuscar.includes(inputBuscar));
      }));
    } else {
      setJustificaciones(datosOriginal);
    }
  };

  const initialColumns = [
    {
      headerName: '',
      field: 'action',
      flex: 0.1,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <>
            <div className='popover-modal-content'>
              <IconButton onClick={() => handleView(row)}>
                <VisibilityIcon />
              </IconButton>
            </div>
          </>);

      }
    },
    {
      field: 'idJustificacion',
      headerName: '#'
    },
    {
      field: 'descDelegacion',
      headerName: 'Delegacion'
    },
    {
      field: 'tipoJustificacion',
      headerName: 'Tipo'
    },
    {
      field: 'idPaciente',
      headerName: 'HC'
    },
    {
      field: 'paciente',
      headerName: 'Paciente'
    },
    {
      field: 'cobertura',
      headerName: 'Cobertura'
    },
    {
      field: 'planCuidado',
      headerName: 'Plan'
    },
    {
      field: 'descEstadoComercial',
      headerName: 'Estado Comercial',
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.descEstadoComercial);
      }
    },
    {
      field: 'descEstadoMedico',
      headerName: 'Estado Medico',
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.descEstadoMedico);
      }
    },
    {
      field: 'descEstadoBUM',
      headerName: 'Estado BUM',
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.descEstadoBUM);
      }
    },
    {
      field: 'fechaAlta',
      headerName: 'Fecha Alta',
      type: 'date'
    },
    {
      field: 'idEvaluacion',
      headerName: 'Contrato'
    },
    {
      field: 'idOrdenPedido',
      headerName: 'Pedido'
    },
    {
      field: 'diasMaxFactCliente',
      headerName: 'Dias Cliente'
    },
    {
      field: 'diasCondiContrato',
      headerName: 'Dias(CO)'
    },
    {
      field: 'contratosCO',
      headerName: 'Contratos(CO)',
      flex: 0.4
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns), [justificaciones]);

  return (
    <>
      <div>
        <Grid
          container
          spacing={2}
          sx={{ marginBottom: '25px' }}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size="small">
              <InputLabel id="select-tipoJustificacion">Tipo Bloqueo</InputLabel>
              <Select
                labelId="select-tipoJustificacion"
                label='Tipo Bloqueo'
                onChange={(e) => {
                  handleChangeFilters('tipoJustificacion', e.target.value);
                }}
                value={filters?.tipoJustificacion}
                name='tipoJustificacion'>

                <MenuItem value="TODOS">Todos</MenuItem>
                <MenuItem value="CONTRATO">Contrato</MenuItem>
                <MenuItem value="PEDIDO">Pedido</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Número Contrato"
              size="small"
              type="text"
              variant="outlined"
              onChange={(e) => {
                handleChangeFilters('idEvaluacion', e.target.value);
              }}              
              name="idEvaluacion"
              value={filters?.idEvaluacion}
              sx={{ backgroundColor: 'white' }}
            />
          </Grid>  
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Número Pedido"
              size="small"
              type="text"
              variant="outlined"
              onChange={(e) => {
                handleChangeFilters('idOrdenPedido', e.target.value);
              }}    
              name="idOrdenPedido"
              value={filters?.idOrdenPedido}
              sx={{ backgroundColor: 'white' }}
            />
          </Grid> */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size="small">
              <InputLabel id="select-idEstadoComercial">Estado Comercial</InputLabel>
              <Select
                labelId="select-idEstadoComercial"
                label='Estado Comercial'
                onChange={(e) => {
                  handleChangeFilters('idEstadoComercial', e.target.value);
                }}    
                value={filters?.idEstadoComercial}
                name='idEstadoComercial'>

                <MenuItem value={0}>Todos</MenuItem>
                {estadosComercial && estadosComercial.map((estadoComercial) => {
                  return <MenuItem key={estadoComercial.idEstadoCondicionalComercial} value={estadoComercial.idEstadoCondicionalComercial}> {estadoComercial.descripcion} </MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size="small">
              <InputLabel id="select-idEstadoMedico">Estado Medico</InputLabel>
              <Select
                labelId="select-idEstadoMedico"
                label='Estado Medico'
                onChange={(e) => {
                  handleChangeFilters('idEstadoMedico', e.target.value);
                }}  
                value={filters?.idEstadoMedico}
                name='idEstadoMedico'>

                <MenuItem value={0}>Todos</MenuItem>
                {estadosMedico && estadosMedico.map((estadoMedico) => {
                  return <MenuItem key={estadoMedico.idEstadoCondicionalMedico} value={estadoMedico.idEstadoCondicionalMedico}> {estadoMedico.descripcion} </MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size="small">
              <InputLabel id="select-idEstadoBUM">Estado BUM</InputLabel>
              <Select
                labelId="select-idEstadoBUM"
                label='Estado BUM'
                onChange={(e) => {
                  handleChangeFilters('idEstadoBUM', e.target.value);
                }}  
                value={filters?.idEstadoBUM}
                name='idEstadoBUM'>

                <MenuItem value={0}>Todos</MenuItem>
                {estadosBUM && estadosBUM.map((estadoBUM) => {
                  return <MenuItem key={estadoBUM.idEstadoCondicionalBUM} value={estadoBUM.idEstadoCondicionalBUM}> {estadoBUM.descripcion} </MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>        
          <Grid size={{ md: 2 }}>
            <TextField
              label="Filtrar"
              name="txtFiltro"
              size="small"
              type="text"
              variant="outlined"
              value={textFilter ?? ''}
              onChange={(e) => setTextFilter(e.target.value)}
              onKeyUp={handleTextFilter}
              sx={{ backgroundColor: 'white', width: '100%' }}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <SearchIcon
                      onClick={handleTextFilter}
                      sx={{ cursor: 'pointer' }} />

                  </InputAdornment>

              }} />
          </Grid>           

          <Grid sx={{ textAlign: 'right' }} size={{ xs: 12, sm: 6, md: 2 }}>
            <CustomButton
              variant='contained'
              className='margin-right'
              onClick={handleSearch}
              size='small'>

              Buscar
            </CustomButton>
          </Grid>
        </Grid>
        <DataGridGenerico
          columns={columns}
          rows={justificaciones ?? []}
          toolbar={false}
          loading={bloqueosLoading}
        />
      </div>
    </>);

};

const excelColumns = [
  "Delegacion",
  "Tipo",
  "Contrato",
  "Pedido",
  "DiasCO",
  "ContratosCO",
  "DiasCliente",
  "HC",
  "Paciente",
  "Cobertura",
  "Plan",
  "EstadoComercial",
  "JustificadoComercial",
  "ObsComercial",
  "FechaComercial",
  "EstadoMedico",
  "JustificadoMedico",
  "ObsMedico",
  "FechaMedico",
  "EstadoBUM",
  "JustificadoBUM",
  "ObsBUM",
  "FechaBUM",
  "FechaAlta"];